import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const DocumentsContainer = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, ${props => props.color || '#007bff'}, ${props => props.color2 || '#0056b3'});
  padding: 1.5rem;
  border-radius: 8px;
  color: white;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
`;

const UploadForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const FileInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background-color: #007bff;
    color: white;

    &:hover {
      background-color: #0069d9;
    }

    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  `}

  ${props => props.variant === 'danger' && `
    background-color: #dc3545;
    color: white;

    &:hover {
      background-color: #c82333;
    }
  `}

  ${props => props.variant === 'success' && `
    background-color: #28a745;
    color: white;

    &:hover {
      background-color: #218838;
    }
  `}

  ${props => props.variant === 'warning' && `
    background-color: #ffc107;
    color: #212529;

    &:hover {
      background-color: #e0a800;
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#cce7ff'};
  background-color: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#e3f2fd'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0d47a1'};
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const DocumentCard = styled.div`
  background: ${props => props.isActive ? '#f8f9fa' : '#fff3cd'};
  padding: 1.5rem;
  border-radius: 8px;
  border: 2px solid ${props => props.isSelected ? '#007bff' : '#dee2e6'};
  transition: all 0.2s;
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const DocumentCheckbox = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
`;

const DocumentStatus = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.isActive ? '#d4edda' : '#f8d7da'};
  color: ${props => props.isActive ? '#155724' : '#721c24'};
  margin-bottom: 0.75rem;
`;

const DocumentTitle = styled.h3`
  font-size: 1.125rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const DocumentMeta = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
`;

const DocumentDescription = styled.p`
  font-size: 0.875rem;
  color: #555;
  margin-bottom: 1.5rem;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background-color: white;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const BatchActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #e9ecef;
  border-radius: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '产品手册',
    type: 'pdf',
    tags: '',
    version: '1.0'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [editingDoc, setEditingDoc] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    version: '',
    isActive: true
  });
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [downloadStats, setDownloadStats] = useState(null);
  const [showDownloadLogs, setShowDownloadLogs] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logFilterDocId, setLogFilterDocId] = useState('');
  const navigate = useNavigate();

  // 无需登录验证，直接访问
  useEffect(() => {
    // 获取文档列表
    fetchDocuments();
    fetchStats();
  }, [navigate]);

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') {
        params.append('category', filterCategory);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(`/api/admin/documents?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (response.data.success) {
        setDocuments(response.data.documents);
      }
    } catch (error) {
      console.error('获取文档列表失败:', error.message);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/documents/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('获取统计信息失败:', error.message);
    }
  };

  const fetchDownloadLogs = async (documentId = '') => {
    setLogLoading(true);
    try {
      const params = new URLSearchParams();
      if (documentId) {
        params.append('documentId', documentId);
      }
      const response = await axios.get(`/api/admin/documents/download-logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (response.data.success) {
        setDownloadLogs(response.data.logs);
      }
    } catch (error) {
      console.error('获取下载记录失败:', error.message);
    } finally {
      setLogLoading(false);
    }
  };

  const fetchDownloadStats = async (documentId = '') => {
    try {
      const params = new URLSearchParams();
      if (documentId) {
        params.append('documentId', documentId);
      }
      const response = await axios.get(`/api/admin/documents/download-stats?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (response.data.success) {
        setDownloadStats(response.data.data);
      }
    } catch (error) {
      console.error('获取下载统计失败:', error.message);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [filterCategory, searchTerm]);

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      if (!selectedFile) {
        setStatus({ type: 'error', message: '请选择要上传的文件' });
        setLoading(false);
        return;
      }

      const form = new FormData();
      form.append('file', selectedFile);
      form.append('title', formData.title);
      form.append('description', formData.description);
      form.append('category', formData.category);
      form.append('type', formData.type);

      const response = await axios.post('/api/admin/documents/upload', form, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        setStatus({ type: 'success', message: '文档上传成功' });
        setFormData({
          title: '',
          description: '',
          category: '产品手册',
          type: 'pdf',
          tags: '',
          version: '1.0'
        });
        setSelectedFile(null);
        e.target.reset(); // 清空文件选择框
        fetchDocuments();
        fetchStats();
      } else {
        setStatus({ type: 'error', message: response.data.message || '上传失败' });
      }
    } catch (error) {
      console.error('上传失败:', error);
      setStatus({
        type: 'error',
        message: error.response?.data?.message || '上传失败，请重试'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除此文档吗？')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/admin/documents/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        setStatus({ type: 'success', message: '文档删除成功' });
        fetchDocuments();
        fetchStats();
      } else {
        setStatus({ type: 'error', message: response.data.message || '删除失败' });
      }
    } catch (error) {
      console.error('删除失败:', error);
      setStatus({ type: 'error', message: '删除失败，请重试' });
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await axios.put(`/api/admin/documents/${id}/toggle`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        setStatus({ type: 'success', message: response.data.message });
        fetchDocuments();
        fetchStats();
      } else {
        setStatus({ type: 'error', message: response.data.message || '操作失败' });
      }
    } catch (error) {
      console.error('操作失败:', error);
      setStatus({ type: 'error', message: '操作失败，请重试' });
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(documents.map(doc => doc._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const handleBatchAction = async (action) => {
    if (selectedIds.length === 0) {
      alert('请先选择要操作的文档');
      return;
    }

    if (action === 'delete' && !window.confirm(`确定要删除选中的 ${selectedIds.length} 个文档吗？`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/admin/documents/batch', {
        ids: selectedIds,
        action
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (response.data.success) {
        setStatus({ type: 'success', message: response.data.message });
        setSelectedIds([]);
        fetchDocuments();
        fetchStats();
      } else {
        setStatus({ type: 'error', message: response.data.message || '操作失败' });
      }
    } catch (error) {
      console.error('批量操作失败:', error);
      setStatus({ type: 'error', message: '批量操作失败，请重试' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (doc) => {
    try {
      const response = await axios.get(`/api/admin/documents/${doc._id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (response.data.success) {
        const docData = response.data.data;
        setEditingDoc(docData);
        setEditFormData({
          title: docData.title,
          description: docData.description,
          category: docData.category,
          tags: docData.tags ? docData.tags.join(', ') : '',
          version: docData.version || '1.0',
          isActive: docData.isActive
        });
        setShowEditModal(true);
      }
    } catch (error) {
      console.error('获取文档详情失败:', error);
      setStatus({ type: 'error', message: '获取文档详情失败' });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`/api/admin/documents/${editingDoc._id}`, editFormData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (response.data.success) {
        setStatus({ type: 'success', message: '文档更新成功' });
        setShowEditModal(false);
        setEditingDoc(null);
        fetchDocuments();
      } else {
        setStatus({ type: 'error', message: response.data.message || '更新失败' });
      }
    } catch (error) {
      console.error('更新失败:', error);
      setStatus({ type: 'error', message: '更新失败，请重试' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const categories = ['产品手册', '使用指南', '技术文档', '运维文档', '其他'];

  return (
    <AdminLayout title="文档管理">
      <DocumentsContainer>
        <Breadcrumb>
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}>仪表盘</a> / 文档管理
          </Breadcrumb>

          <Header>
            <Title>文档管理</Title>
          </Header>

          {status && <Alert type={status.type}>{status.message}</Alert>}

      {stats && (
        <Section>
          <SectionTitle>统计信息</SectionTitle>
          <StatsGrid>
            <StatCard color="#007bff" color2="#0056b3">
              <StatValue>{stats.total}</StatValue>
              <StatLabel>总文档数</StatLabel>
            </StatCard>
            <StatCard color="#28a745" color2="#218838">
              <StatValue>{stats.active}</StatValue>
              <StatLabel>已发布</StatLabel>
            </StatCard>
            <StatCard color="#ffc107" color2="#e0a800">
              <StatValue>{stats.inactive}</StatValue>
              <StatLabel>已下架</StatLabel>
            </StatCard>
            <StatCard color="#17a2b8" color2="#138496">
              <StatValue>{stats.totalDownloads}</StatValue>
              <StatLabel>总下载次数</StatLabel>
            </StatCard>
          </StatsGrid>
        </Section>
      )}

      <Section>
        <SectionTitle>上传文档</SectionTitle>
        <UploadForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="title">文档标题</Label>
            <Input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="输入文档标题"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">文档描述</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="输入文档描述"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="category">文档分类</Label>
            <Select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="type">文档类型</Label>
            <Select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
            >
              <option value="pdf">PDF</option>
              <option value="doc">Word</option>
              <option value="xls">Excel</option>
              <option value="ppt">PowerPoint</option>
              <option value="zip">ZIP</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="file">选择文件</Label>
            <FileInput
              type="file"
              id="file"
              onChange={handleFileChange}
              required
            />
            {selectedFile && (
              <small style={{ color: '#666', marginTop: '0.5rem' }}>
                已选择文件: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </small>
            )}
          </FormGroup>

          <ButtonGroup>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? '上传中...' : '上传文档'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => {
              setFormData({
                title: '',
                description: '',
                category: '产品手册',
                type: 'pdf'
              });
              setSelectedFile(null);
            }}>
              重置
            </Button>
          </ButtonGroup>
        </UploadForm>
      </Section>

      <Section>
        <SectionTitle>文档列表</SectionTitle>
        {selectedIds.length > 0 && (
          <BatchActions>
            <span>已选择 {selectedIds.length} 个文档</span>
            <Button 
              variant="success" 
              onClick={() => handleBatchAction('publish')}
              disabled={loading}
            >
              批量发布
            </Button>
            <Button 
              variant="warning" 
              onClick={() => handleBatchAction('unpublish')}
              disabled={loading}
            >
              批量下架
            </Button>
            <Button 
              variant="danger" 
              onClick={() => handleBatchAction('delete')}
              disabled={loading}
            >
              批量删除
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => setSelectedIds([])}
            >
              取消选择
            </Button>
          </BatchActions>
        )}

        <SearchBar>
          <SearchInput
            type="text"
            placeholder="搜索文档标题或描述..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterSelect
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">全部分类</option>
            {categories.map((category) => (
              <option key={category} value={category}>{category}</option>
            ))}
          </FilterSelect>
          <CheckboxLabel>
            <Checkbox
              type="checkbox"
              checked={selectedIds.length === documents.length && documents.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            全选
          </CheckboxLabel>
        </SearchBar>
        <DocumentsGrid>
          {documents.map((doc) => (
            <DocumentCard key={doc._id} isActive={doc.isActive} isSelected={selectedIds.includes(doc._id)}>
              <DocumentCheckbox>
                <Checkbox
                  type="checkbox"
                  checked={selectedIds.includes(doc._id)}
                  onChange={(e) => handleSelect(doc._id, e.target.checked)}
                />
              </DocumentCheckbox>
              <DocumentTitle>{doc.title}</DocumentTitle>
              <DocumentStatus isActive={doc.isActive}>
                {doc.isActive ? '已发布' : '已下架'}
              </DocumentStatus>
              <DocumentMeta>
                <span>📁 {doc.category}</span>
                <span>📄 {doc.type.toUpperCase()}</span>
                <span>📊 {doc.sizeText}</span>
                <span>⬇️ {doc.downloadCount || 0} 次下载</span>
              </DocumentMeta>
              <DocumentDescription>{doc.description}</DocumentDescription>
              <DocumentActions>
                <Button variant="primary" onClick={() => handleEdit(doc)}>
                  编辑
                </Button>
                <Button 
                  variant={doc.isActive ? 'warning' : 'success'} 
                  onClick={() => handleToggle(doc._id)}
                >
                  {doc.isActive ? '下架' : '发布'}
                </Button>
                <Button variant="danger" onClick={() => handleDelete(doc._id)}
                >
                  删除
                </Button>
              </DocumentActions>
            </DocumentCard>
          ))}
        </DocumentsGrid>

        {documents.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📭</div>
            <p>暂无文档</p>
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>下载记录</SectionTitle>
        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant={showDownloadLogs ? 'primary' : 'secondary'} 
            onClick={() => {
              setShowDownloadLogs(!showDownloadLogs);
              if (!showDownloadLogs) {
                fetchDownloadLogs(logFilterDocId);
                fetchDownloadStats(logFilterDocId);
              }
            }}
          >
            {showDownloadLogs ? '收起下载记录' : '查看下载记录'}
          </Button>
          <FilterSelect 
            value={logFilterDocId} 
            onChange={(e) => setLogFilterDocId(e.target.value)}
            style={{ minWidth: '200px' }}
          >
            <option value="">全部文档</option>
            {documents.map(doc => (
              <option key={doc._id} value={doc._id}>{doc.title}</option>
            ))}
          </FilterSelect>
        </div>

        {showDownloadLogs && (
          <>
            {downloadStats && (
              <StatsGrid style={{ marginBottom: '1.5rem' }}>
                <StatCard color="#6f42c1" color2="#5a3d8a">
                  <StatValue>{downloadStats.totalDownloads}</StatValue>
                  <StatLabel>总下载次数</StatLabel>
                </StatCard>
                {downloadStats.downloadsByDocument && downloadStats.downloadsByDocument.length > 0 && (
                  <StatCard color="#fd7e14" color2="#e55a0a">
                    <StatValue>{downloadStats.downloadsByDocument[0].title?.substring(0, 15) || 'N/A'}</StatValue>
                    <StatLabel>最热门文档（{downloadStats.downloadsByDocument[0].count}次）</StatLabel>
                  </StatCard>
                )}
                {downloadStats.downloadsByUser && downloadStats.downloadsByUser.length > 0 && (
                  <StatCard color="#20c997" color2="#199d76">
                    <StatValue>{downloadStats.downloadsByUser[0].userName || '访客'}</StatValue>
                    <StatLabel>最高下载用户（{downloadStats.downloadsByUser[0].count}次）</StatLabel>
                  </StatCard>
                )}
              </StatsGrid>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>下载排行榜（Top 10）</h4>
              {downloadStats?.downloadsByDocument && downloadStats.downloadsByDocument.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {downloadStats.downloadsByDocument.map((doc, index) => (
                    <div key={doc.documentId} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f8f9fa', borderRadius: '4px' }}>
                      <span>{index + 1}. {doc.title}</span>
                      <span style={{ fontWeight: 'bold', color: '#007bff' }}>{doc.count}次</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#666' }}>暂无数据</div>
              )}
            </div>

            {logLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>加载中...</div>
            ) : downloadLogs.length > 0 ? (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>文档名称</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>用户</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>IP地址</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>时间</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>文件大小</th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloadLogs.map((log, index) => (
                      <tr key={log._id || index} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '0.75rem' }}>{log.documentTitle}</td>
                        <td style={{ padding: '0.75rem' }}>{log.userName || '访客'}</td>
                        <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{log.userIP || '未知'}</td>
                        <td style={{ padding: '0.75rem' }}>{new Date(log.downloadTime).toLocaleString('zh-CN')}</td>
                        <td style={{ padding: '0.75rem' }}>{log.fileSize ? `${(log.fileSize / 1024 / 1024).toFixed(2)} MB` : '未知'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📋</div>
                <p>暂无下载记录</p>
              </div>
            )}
          </>
        )}
      </Section>

      <ModalOverlay show={showEditModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>编辑文档</ModalTitle>
            <CloseButton onClick={() => {
              setShowEditModal(false);
              setEditingDoc(null);
            }}>&times;</CloseButton>
          </ModalHeader>
          <UploadForm onSubmit={handleEditSubmit}>
            <FormGroup>
              <Label htmlFor="edit-title">文档标题</Label>
              <Input
                type="text"
                id="edit-title"
                name="title"
                value={editFormData.title}
                onChange={handleEditInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="edit-description">文档描述</Label>
              <Textarea
                id="edit-description"
                name="description"
                value={editFormData.description}
                onChange={handleEditInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="edit-category">文档分类</Label>
              <Select
                id="edit-category"
                name="category"
                value={editFormData.category}
                onChange={handleEditInputChange}
                required
              >
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="edit-tags">标签（用逗号分隔）</Label>
              <Input
                type="text"
                id="edit-tags"
                name="tags"
                value={editFormData.tags}
                onChange={handleEditInputChange}
                placeholder="例如: 技术, 教程, 入门"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="edit-version">版本号</Label>
              <Input
                type="text"
                id="edit-version"
                name="version"
                value={editFormData.version}
                onChange={handleEditInputChange}
                placeholder="例如: 1.0"
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="isActive"
                  checked={editFormData.isActive}
                  onChange={handleEditInputChange}
                />
                启用文档（取消勾选将隐藏文档）
              </CheckboxLabel>
            </FormGroup>

            <ButtonGroup>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? '保存中...' : '保存修改'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => {
                setShowEditModal(false);
                setEditingDoc(null);
              }}>
                取消
              </Button>
            </ButtonGroup>
          </UploadForm>
        </ModalContent>
      </ModalOverlay>
    </DocumentsContainer>
    </AdminLayout>
  );
};

export default DocumentsPage;